import { useEffect, useMemo, useState } from "react";
import { Building2, MapPin } from "lucide-react";
import Modal from "../ui/Modal";
import SelectField from "../controls/SelectField";
import { createHotel } from "../../api/hotels";
import { HotelCreateInput, HotelType } from "../../types/hotels";
import { OtaKey } from "../../types/ota";
import OtaUrlsFields, {
  buildOtaUrlsPayload,
  countFilledOtaUrls,
  EMPTY_OTA_VALUES,
  getInvalidOtaKeys,
  OtaValues,
} from "../ota/OtaUrlsFields";
import { buildCitySelectOptions } from "../../constants/cities";

const TYPE_OPTIONS: HotelType[] = ["Отель", "Сетевой отель", "Апартаменты", "Гостевой дом", "Коттедж, вилла"];
const OBJECTS_LIMIT_REACHED_MESSAGE = "Достигнут лимит объектов размещения для компании";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  companyId: number;
  citySuggestions?: string[];
  otaLimit?: number | null;
};

export default function AddHotelModal({
  open,
  onClose,
  onCreated,
  companyId,
  citySuggestions = [],
  otaLimit,
}: Props) {
  const [name, setName] = useState("");
  const [type, setType] = useState<HotelType | "">("");
  const [citySelect, setCitySelect] = useState<string>("");
  const [cityManual, setCityManual] = useState<string>("");
  const [otaUrls, setOtaUrls] = useState<OtaValues>(EMPTY_OTA_VALUES);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isManualCity = citySelect === "__manual__";
  const limit = Math.max(1, Number.isFinite(otaLimit ?? NaN) ? Number(otaLimit) : 3);
  const filledOtaCount = useMemo(() => countFilledOtaUrls(otaUrls), [otaUrls]);
  const isOverOtaLimit = filledOtaCount > limit;

  const typeOptions = useMemo(() => TYPE_OPTIONS.map(v => ({ label: v, value: v })), []);
  const cityOptions = useMemo(
    () => [
      ...buildCitySelectOptions(citySuggestions),
      { label: "Ввести вручную…", value: "__manual__" },
    ],
    [citySuggestions]
  );

  useEffect(() => {
    if (!open) {
      setName("");
      setType("");
      setCitySelect("");
      setCityManual("");
      setOtaUrls(EMPTY_OTA_VALUES);
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60";

  function handleOtaUrlChange(key: OtaKey, value: string) {
    setOtaUrls((current) => ({
      ...current,
      [key]: value,
    }));
    setError("");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const cityValue = isManualCity ? cityManual.trim() : citySelect.trim();
    if (!name.trim()) return setError("Введите название");
    if (!type) return setError("Выберите тип");
    if (!cityValue) return setError("Укажите город");
    if (getInvalidOtaKeys(otaUrls).size > 0) {
      return setError("Проверьте ссылки ОР: одна или несколько ссылок не соответствуют примеру");
    }
    if (filledOtaCount === 0) {
      return setError("Добавьте хотя бы одну OTA-ссылку для объекта размещения");
    }
    if (isOverOtaLimit) {
      return setError(`Превышен лимит OTA-ссылок для объекта размещения: разрешено ${limit}, указано ${filledOtaCount}`);
    }

    setSubmitting(true);
    try {
      const payload: HotelCreateInput = {
        name: name.trim(),
        type: type as HotelType,
        city: cityValue,
        ota_urls: buildOtaUrlsPayload(otaUrls),
      };
      await createHotel(companyId, payload);
      onCreated();
      onClose();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setError(e?.response?.data?.detail || OBJECTS_LIMIT_REACHED_MESSAGE);
      } else {
        setError(e?.response?.data?.detail || "Не удалось добавить объект");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-5xl">
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="text-center space-y-3 animate-softFade">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner">
            <Building2 size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Добавить объект</h3>
            <p className="mt-1 text-sm text-slate-500">Укажите данные, чтобы объект появился в списке компании</p>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Название *</span>
            <div className="relative">
              <Building2 size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, Отель Ромашка"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </label>

          <OtaUrlsFields
            values={otaUrls}
            onChange={handleOtaUrlChange}
            disabled={submitting}
            officialLabel="Сайт отеля"
            title="Маппинг *"
          />

          <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">
            Максимум ОТА для объекта: {limit}
          </div>

          {isOverOtaLimit ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              Превышен лимит OTA-ссылок для объекта размещения: разрешено {limit}, указано {filledOtaCount}.
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">Тип *</span>
              <SelectField
                value={type}
                onChange={(v) => setType(v as HotelType)}
                placeholder="Выберите тип"
                icon={<Building2 size={18} />}
                options={typeOptions}
                className="w-full border-slate-200/80 bg-white/80 shadow-inner"
              />
            </label>

            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-600">Город *</span>
              <SelectField
                value={citySelect}
                onChange={(v) => setCitySelect(String(v))}
                placeholder="Выберите город"
                icon={<MapPin size={18} />}
                options={cityOptions}
                className="w-full border-slate-200/80 bg-white/80 shadow-inner"
              />
            </label>
          </div>

          {isManualCity && (
            <input
              className={`${baseInputClass} mt-1`}
              placeholder="Введите город вручную *"
              value={cityManual}
              onChange={(e) => setCityManual(e.target.value)}
            />
          )}
        </div>

        {error && (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            disabled={submitting || isOverOtaLimit}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none"
          >
            {submitting ? "Добавляем…" : "Добавить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
